import { isEqual } from 'lodash-es';
import { useRef } from 'react';

import type { FilterFieldProps } from '../FilterField.types.js';
import type { FilterFieldTree } from '../types/tree-nodes.js';
import { convertLezerTree } from '../utils/conversion/tree-conversion.js';
import type { InternalValidatorMap } from '../utils/validation-utils/normalize-validator-map.js';

/** @internal */
export function useCachedTreeConversion() {
  const prevDoc = useRef<string | undefined>(undefined);
  const cachedTree = useRef<FilterFieldTree | undefined>(undefined);
  const cachedIsValid = useRef(true);
  const cachedIsValidSearch = useRef(true);
  const prevInternalValidatorMap = useRef<InternalValidatorMap | undefined>(
    undefined,
  );
  const prevParserConfig = useRef<FilterFieldProps['parserConfig']>(undefined);

  function getSyntaxTree(
    args: Parameters<typeof convertLezerTree>[0],
  ): ReturnType<typeof convertLezerTree> {
    const { doc, internalValidatorMap, parserConfig } = args;

    // If the document and syntax mode didn't change and we already have a converted tree, we can return the cached tree.
    if (
      prevDoc.current === doc?.toString() &&
      // The internalValidatorMap is derived from the validatorMap prop, which is normalized with useMemo in FilterField.tsx
      // whenever the validatorMap changes. It has a stable reference as long as the raw validatorMap doesn't change.
      // Therefore, we can rely on reference equality here for caching.
      internalValidatorMap === prevInternalValidatorMap.current &&
      isEqual(parserConfig, prevParserConfig.current) &&
      cachedTree.current
    ) {
      return {
        tree: cachedTree.current,
        isValid: cachedIsValid.current,
        isValidSearch: cachedIsValidSearch.current,
      };
    }

    prevDoc.current = doc?.toString();
    prevInternalValidatorMap.current = internalValidatorMap;
    prevParserConfig.current = parserConfig;
    const filter = convertLezerTree(args);
    cachedTree.current = filter.tree;
    cachedIsValid.current = filter.isValid;
    cachedIsValidSearch.current = filter.isValidSearch;

    return filter;
  }

  return { getSyntaxTree };
}
